/**
 * Centralized environment configuration.
 *
 * NEXT_PUBLIC_* vars use static `process.env.NEXT_PUBLIC_X` access so
 * Turbopack can inline them into client bundles at build time.
 *
 * Build-time vars (NEXT_PUBLIC_*) → Cloudflare Pages dashboard.
 * Runtime vars (BACKEND_URL, SEEKER_URL) → wrangler.toml [vars] or secrets.
 */

const isServer = typeof window === 'undefined';
const isDev = process.env.NODE_ENV === 'development';
const isTest = process.env.NODE_ENV === 'test';

// Next.js sets this during `next build` — lets us skip runtime-only
// validation that would crash the build's static generation phase.
const isBuild = process.env.NEXT_PHASE === 'phase-production-build';

function required(value: string | undefined, name: string): string {
  if (value) return value;
  if (isBuild || isDev || isTest) return '';
  throw new Error(
    `[env] ${name} is required in production. Set via Cloudflare dashboard or wrangler secret.`,
  );
}

function optional(name: string, fallback = ''): string {
  return process.env[name] ?? fallback;
}

export const env = {
  NODE_ENV: process.env.NODE_ENV ?? 'development',

  // ── Supabase (required — inlined at build time) ────────────────────
  NEXT_PUBLIC_SUPABASE_URL: required(
    process.env.NEXT_PUBLIC_SUPABASE_URL,
    'NEXT_PUBLIC_SUPABASE_URL',
  ),
  NEXT_PUBLIC_SUPABASE_ANON_KEY: required(
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY,
    'NEXT_PUBLIC_SUPABASE_ANON_KEY',
  ),

  // ── Backend URLs (runtime-only, injected by Workers) ───────────────
  get BACKEND_URL(): string {
    const val =
      process.env.BACKEND_URL ??
      process.env.NEXT_API_BASE_URL ??
      (isDev ? 'http://localhost:8000' : '');
    if (!val && !isBuild) {
      throw new Error(
        '[env] BACKEND_URL is required. Set via wrangler secret or deployment config.',
      );
    }
    return val;
  },

  get SEEKER_URL(): string {
    const val = process.env.SEEKER_URL ?? (isDev ? 'http://localhost:8001' : '');
    if (!val && !isBuild) {
      throw new Error(
        '[env] SEEKER_URL is required. Set via wrangler secret or deployment config.',
      );
    }
    return val;
  },

  // ── CORS ───────────────────────────────────────────────────────────
  ALLOWED_ORIGINS: optional('ALLOWED_ORIGINS', isDev ? 'http://localhost:3000' : ''),

  // ── Observability (optional — disabled if missing) ─────────────────
  NEXT_PUBLIC_SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN ?? '',
  NEXT_PUBLIC_SENTRY_ENVIRONMENT:
    process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT ?? process.env.NODE_ENV ?? 'development',
  NEXT_PUBLIC_POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY ?? '',
  NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST ?? 'https://e.serenoty.com',

  // ── App ────────────────────────────────────────────────────────────
  NEXT_PUBLIC_APP_URL:
    process.env.NEXT_PUBLIC_APP_URL ?? (isDev ? 'http://localhost:3000' : 'https://app.virza.io'),
  NEXT_PUBLIC_APP_VERSION: process.env.NEXT_PUBLIC_APP_VERSION ?? '1.0.0',
  NEXT_PUBLIC_API_MODE: process.env.NEXT_PUBLIC_API_MODE ?? 'mock',

  // ── Turnstile (optional) ───────────────────────────────────────────
  NEXT_PUBLIC_TURNSTILE_SITE_KEY: process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? '',
  TURNSTILE_SECRET_KEY: isServer ? optional('TURNSTILE_SECRET_KEY') : '',

  // ── Realtime ───────────────────────────────────────────────────────
  NEXT_PUBLIC_REALTIME_URL: process.env.NEXT_PUBLIC_REALTIME_URL ?? '',

  // ── Maintenance ────────────────────────────────────────────────────
  MAINTENANCE_MODE: isServer ? optional('MAINTENANCE_MODE', 'false') : 'false',
  MAINTENANCE_RETRY_AFTER: isServer ? optional('MAINTENANCE_RETRY_AFTER', '3600') : '3600',

  // ── Auth Cookie Isolation ───────────────────────────────────────────
  // Suffix added to Supabase auth cookie names to prevent cross-deployment
  // token rotation conflicts. Required when multiple deployments share the
  // same Supabase project (staging, preview-staging, localhost).
  // Production: not set (no suffix). Staging: "stg". Preview: "pvw". Dev: "dev".
  NEXT_PUBLIC_AUTH_COOKIE_SUFFIX: process.env.NEXT_PUBLIC_AUTH_COOKIE_SUFFIX ?? '',

  // ── Dev/Test ───────────────────────────────────────────────────────
  NEXT_PUBLIC_MOCK_ERRORS: process.env.NEXT_PUBLIC_MOCK_ERRORS ?? 'false',
} as const;
